import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight, Mail, Phone } from "lucide-react";
import { API } from "../../App";
import { GOLD, GOLD_DARK, NAVY, Overline, Reveal } from "./ui";
import { BUSINESS } from "../../data/business/profile";

const FIELD =
  "w-full px-0 py-2.5 bg-transparent border-0 border-b-2 focus:outline-none transition-colors";
const FIELD_OK = "border-slate-200 focus:border-[color:var(--gold)]";
const FIELD_ERR = "border-red-500 focus:border-red-500";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;

const validate = (f) => {
  const e = {};
  if (!f.name.trim()) e.name = "Skriv dit navn, så vi ved hvem vi skal spørge efter.";
  if (!f.phone.trim()) e.phone = "Skriv et telefonnummer, vi kan ringe til.";
  else if (f.phone.replace(/\D/g, "").length < 8) e.phone = "Telefonnummeret skal indeholde mindst 8 cifre.";
  if (!f.email.trim()) e.email = "Skriv din e-mail, så vi kan sende oplægget skriftligt.";
  else if (!EMAIL_RE.test(f.email.trim())) e.email = "E-mailen ser ikke rigtig ud. Tjek at der er et @ og et domæne.";
  return e;
};

const FieldError = ({ id, msg }) =>
  msg ? (
    <span id={id} role="alert" className="block text-xs font-medium text-red-600 mt-2" data-testid={id}>
      {msg}
    </span>
  ) : null;

/** Driftsgennemgang-formular. Bruges på forsiden og /business/kontakt. */
const BusinessLeadForm = ({ t = {}, id = "kontakt" }) => {
  const [form, setForm] = useState({ name: "", company: "", phone: "", email: "", trucks: "", message: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (key, value) => {
    setForm((p) => ({ ...p, [key]: value }));
    if (errors[key]) setErrors((p) => ({ ...p, [key]: undefined }));
  };
  const fieldProps = (key) => ({
    "aria-invalid": errors[key] ? true : undefined,
    "aria-describedby": errors[key] ? `business-${key}-error` : undefined,
    className: `${FIELD} ${errors[key] ? FIELD_ERR : FIELD_OK}`,
  });

  const submit = async (e) => {
    e.preventDefault();
    const found = validate(form);
    setErrors(found);
    const first = Object.keys(found)[0];
    if (first) {
      document.querySelector(`[data-testid="business-${first}-input"]`)?.focus();
      return;
    }
    setSubmitting(true);
    const details = [
      form.company.trim() && `Virksomhed: ${form.company.trim()}`,
      form.trucks.trim() && `Antal biler: ${form.trucks.trim()}`,
      form.message.trim(),
    ].filter(Boolean).join("\n");
    try {
      await axios.post(`${API}/business/lead`, {
        name: form.name, phone: form.phone, email: form.email, message: details,
      });
      setSubmitted(true);
      setForm({ name: "", company: "", phone: "", email: "", trucks: "", message: "" });
      toast.success("Tak! Vi vender tilbage inden for en hverdag.");
    } catch {
      toast.error(`Kunne ikke sende forespørgslen. Prøv igen eller skriv til ${BUSINESS.email}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id={id} className="py-20 md:py-28 bg-slate-50" data-testid="business-contact-section">
      <div className="max-w-[1680px] mx-auto px-4 md:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <Reveal className="lg:col-span-5">
            <Overline>{t.sectionLabel || "Kontakt"}</Overline>
            <h2 className="font-display text-3xl md:text-4xl lg:text-[2.75rem] font-medium text-slate-900 leading-[1.12] mb-5">
              {t.sectionTitle || "Få en driftsgennemgang"}
            </h2>
            <p className="text-slate-600 text-base md:text-lg leading-relaxed mb-9">
              {t.sectionSubtitle ||
                "Vi gennemgår din forretning og lægger et konkret oplæg frem: hvad vi overtager, hvad det kræver af dig, og hvad det koster. Uden forpligtelse."}
            </p>
            <div className="space-y-4">
              <a href={`mailto:${BUSINESS.email}`} className="flex items-center gap-3 text-slate-700 hover:text-slate-900 transition-colors" data-testid="contact-mail-link">
                <span className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: "rgba(212,175,55,0.12)" }}>
                  <Mail className="h-5 w-5" style={{ color: GOLD_DARK }} aria-hidden />
                </span>
                {BUSINESS.email}
              </a>
              <a href={`tel:${BUSINESS.phoneHref}`} className="flex items-center gap-3 text-slate-700 hover:text-slate-900 transition-colors" data-testid="contact-phone-link">
                <span className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: "rgba(212,175,55,0.12)" }}>
                  <Phone className="h-5 w-5" style={{ color: GOLD_DARK }} aria-hidden />
                </span>
                {BUSINESS.phone}
              </a>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-7" i={1}>
            <div className="bg-white rounded-3xl shadow-xl border border-slate-100 p-7 md:p-10 relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ backgroundColor: GOLD }} />
              {submitted ? (
                <div className="text-center py-10" data-testid="business-form-success">
                  <CheckCircle2 className="h-14 w-14 mx-auto mb-4" style={{ color: GOLD }} aria-hidden />
                  <h3 className="font-display text-2xl font-medium text-slate-900 mb-2">{t.successTitle || "Tak for din henvendelse!"}</h3>
                  <p className="text-slate-600 mb-6">{t.successText || "Vi vender tilbage inden for en hverdag på telefon eller mail."}</p>
                  <button onClick={() => setSubmitted(false)} className="font-semibold hover:underline" style={{ color: GOLD_DARK }}
                    data-testid="business-form-reset-btn">
                    Send en ny henvendelse
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} noValidate className="space-y-6" data-testid="business-lead-form" style={{ "--gold": GOLD }}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <label className="block">
                      <span className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{t.labels?.name || "Navn"} *</span>
                      <input type="text" value={form.name} onChange={(e) => set("name", e.target.value)}
                        {...fieldProps("name")} placeholder="Fornavn Efternavn" data-testid="business-name-input" />
                      <FieldError id="business-name-error" msg={errors.name} />
                    </label>
                    <label className="block">
                      <span className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{t.labels?.company || "Virksomhed"}</span>
                      <input type="text" value={form.company} onChange={(e) => set("company", e.target.value)}
                        {...fieldProps("company")} placeholder="Firmanavn eller CVR" data-testid="business-company-input" />
                    </label>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <label className="block">
                      <span className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{t.labels?.phone || "Telefon"} *</span>
                      <input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)}
                        {...fieldProps("phone")} placeholder="+45 ..." data-testid="business-phone-input" />
                      <FieldError id="business-phone-error" msg={errors.phone} />
                    </label>
                    <label className="block">
                      <span className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{t.labels?.email || "Email"} *</span>
                      <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
                        {...fieldProps("email")} placeholder="din@email.dk" data-testid="business-email-input" />
                      <FieldError id="business-email-error" msg={errors.email} />
                    </label>
                  </div>
                  <label className="block">
                    <span className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{t.labels?.trucks || "Antal biler"}</span>
                    <input type="text" value={form.trucks} onChange={(e) => set("trucks", e.target.value)}
                      {...fieldProps("trucks")} placeholder="Fx 3 lastbiler og 1 varebil" data-testid="business-trucks-input" />
                  </label>
                  <label className="block">
                    <span className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{t.labels?.message || "Hvad sidder du selv med i dag?"}</span>
                    <textarea rows={4} value={form.message} onChange={(e) => set("message", e.target.value)}
                      {...fieldProps("message")} placeholder="Fx bogføring, løn, fakturering, papirer på chaufførerne ..."
                      data-testid="business-message-input" />
                  </label>
                  <button type="submit" disabled={submitting}
                    className="group w-full py-4 rounded-full font-semibold transition-all hover:shadow-lg disabled:opacity-60 inline-flex items-center justify-center gap-2"
                    style={{ backgroundColor: GOLD, color: NAVY }} data-testid="business-submit-btn">
                    {submitting ? "Sender..." : (t.submitButton || "Book driftsgennemgang")}
                    {!submitting && <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" aria-hidden />}
                  </button>
                  <p className="text-center text-sm text-slate-500 pt-1">
                    {t.helperText || "Vi vender tilbage inden for en hverdag. Ingen forpligtelser."}
                  </p>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default BusinessLeadForm;
